import * as React from 'react'
import cn from 'classnames'

type RemoveCycleDialogProps = {
  removing: boolean
  onCancel: () => void
  onConfirm: () => void
}

// A modal confirmation for the irreversible removal of an archived cycle. The
// overlay blocks the page behind it; clicking it (or Esc) cancels.
export const RemoveCycleDialog = ({
  removing,
  onCancel,
  onConfirm,
}: RemoveCycleDialogProps) => {
  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onCancel])

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="remove-cycle-title"
      onClick={onCancel}
      className="fixed inset-0 z-[60] flex items-center justify-center bg-[rgba(29,38,74,0.22)] px-4"
    >
      <div
        onClick={(event) => event.stopPropagation()}
        className="w-full max-w-[460px] rounded-[12px] bg-white p-7 shadow-[0_18px_44px_rgba(29,38,74,0.18)]"
      >
        <h3
          id="remove-cycle-title"
          className="font-talent text-[21px] font-medium leading-[26px] text-[#1D264A]"
        >
          Are you sure you want to remove this cycle?
        </h3>
        <p className="mt-2 text-[16px] leading-[22px] text-[#5E6580]">
          Once the cycle is removed you can not undo the action.
        </p>

        <div className="mt-7 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={removing}
            className="inline-flex h-[44px] items-center justify-center rounded-lg border border-[#D7DBE8] bg-white px-6 text-[16px] font-semibold text-[#1D264A] hover:bg-[#F7F8FB]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={removing}
            className={cn(
              'inline-flex h-[44px] items-center justify-center rounded-lg px-6 text-[16px] font-semibold text-white transition-opacity',
              removing
                ? 'bg-[#E42309] opacity-50'
                : 'bg-[#E42309] hover:bg-[#cc1f08]'
            )}
          >
            {removing ? 'Removing…' : 'Remove'}
          </button>
        </div>
      </div>
    </div>
  )
}
